import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScaleRangeStepper } from "components/__haim/Common/ScaleStepper";
import { rooms as roomsScale } from "../../../@types/sharedSchema";

export interface RoomRangePickerProps {
  initialMin?: number;
  initialMax?: number;
  disabled?: boolean;
  onRangeChange?: (min: number | undefined, max: number | undefined) => void;
}

/**
 * Rooms min/max as +/− steppers, matching the contact dialog's control.
 *
 * Replaces the old pill row, whose click-to-start / click-to-end state machine
 * made "3 to 3" and "any" hard to express and let a stale single selection sit
 * around looking like a range. The stepper pair cannot be inverted — see
 * ScaleRangeStepper for the correction rules.
 *
 * The scale is the shared `rooms` enum, so Israeli half steps (1, 1.5, 2, …)
 * come for free rather than being hardcoded here.
 */
export default function RoomRangePicker({
  initialMin,
  initialMax,
  disabled,
  onRangeChange
}: RoomRangePickerProps) {
  const { t } = useTranslation();
  const [range, setRange] = useState<{ min: number | null; max: number | null }>({
    min: initialMin ?? null,
    max: initialMax ?? null
  });

  const handleChange = (min: number | null, max: number | null): void => {
    setRange({ min, max });
    onRangeChange?.(min ?? undefined, max ?? undefined);
  };

  return (
    <ScaleRangeStepper
      scale={roomsScale as unknown as number[]}
      min={range.min}
      max={range.max}
      disabled={disabled}
      minLabel={t("listingsMatch.filters.minRoomsLabel", { defaultValue: "מ־" })}
      maxLabel={t("listingsMatch.filters.maxRoomsLabel", { defaultValue: "עד" })}
      formatValue={(value) => (value === roomsScale[roomsScale.length - 1] ? `${value}+` : value)}
      onChange={handleChange}
    />
  );
}
